import React, { useState, useEffect, useCallback } from "react";
import {
  addTestimonial,
  updateTestimonial,
  getTestimonialById,
} from "../../../services/testimonialsService";
import { validateTestimonialMaster } from "../../../utils/validation";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleErrors } from "../../../utils/errorHandler";
import FormSideDrawer from "../../Common/FormSideDrawer/FormSideDrawer";
import { useMediaLibraryIntegration } from "../../MediaLibrary/MediaSourcePicker";
import { uploadDroppedMediaFile } from "../../MediaLibrary/mediaLibraryUploadUtils";
import "./testimonial-image-upload.css";

const INITIAL_FORM_STATE = {
  personName: "",
  personDesignation: "",
  testimonialText: "",
  displayOrder: "",
  imageFile: null,
  imagePreview: "",
  personImageUrl: "",
};

const INITIAL_ERRORS_STATE = {
  personName: "",
  personDesignation: "",
  testimonialText: "",
  personImageUrl: "",
  displayOrder: "",
};

const buildTestimonialFormData = (formData, editMode, id) => {
  const payload = new FormData();
  if (editMode) {
    payload.append("Id", id);
  }
  payload.append("PersonName", formData.personName.trim());
  if (formData.personDesignation?.trim()) {
    payload.append("PersonDesignation", formData.personDesignation.trim());
  }
  payload.append("TestimonialText", formData.testimonialText.trim());
  if (formData.personImageUrl) {
    payload.append("PersonImageUrl", String(formData.personImageUrl));
  }
  payload.append("DisplayOrder", formData.displayOrder);
  return payload;
};

const mapTestimonialResponse = (data) => ({
  personName: data.personName || data.PersonName || "",
  personDesignation: data.personDesignation || data.PersonDesignation || "",
  testimonialText: data.testimonialText || data.TestimonialText || "",
  displayOrder:
    data.displayOrder !== undefined && data.displayOrder !== null
      ? String(data.displayOrder)
      : data.DisplayOrder !== undefined && data.DisplayOrder !== null
        ? String(data.DisplayOrder)
        : "",
  imagePreview:
    data.personImageUrl || data.PersonImageUrl || data.imageUrl || "",
  imageFile: null,
});

export const AddTestimonial = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedTestimonial,
  setEditMode,
  isOpen = false,
  onClose,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isImageDragging, setIsImageDragging] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_STATE);
    setErrors(INITIAL_ERRORS_STATE);
    setSelectedMedia(null);
  }, []);

  const closeDrawer = useCallback(() => {
    resetForm();
    setSelectedTestimonial?.(null);
    setEditMode?.(false);
    onClose?.();
  }, [resetForm, setSelectedTestimonial, setEditMode, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchData = async () => {
      const testimonialId = initialData?.id ?? initialData?.Id;
      if (editMode && testimonialId) {
        try {
          const data = await getTestimonialById(testimonialId);
          setFormData(mapTestimonialResponse(data));
          setSelectedMedia(null);
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
        setSelectedMedia(null);
      }
      setErrors(INITIAL_ERRORS_STATE);
    };
    fetchData();
  }, [editMode, initialData, isOpen]);

  const handleInputChange = ({ target: { name, value } }) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleDisplayOrderChange = (e) => {
    const { value } = e.target;
    if (value === "" || /^[1-9]\d*$/.test(value)) {
      setFormData((prevData) => ({ ...prevData, displayOrder: value }));
      setErrors((prevErrors) => ({ ...prevErrors, displayOrder: "" }));
    }
  };

  const handleDisplayOrderKeyDown = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleMediaSelect = (media) => {
    const url = media?.mediaUrl || media?.thumbnailUrl || "";
    if (!url) return;
    setFormData((prevData) => ({
      ...prevData,
      imageFile: null,
      imagePreview: url,
      personImageUrl: url,
    }));
    setSelectedMedia(media);
    setErrors((prevErrors) => ({ ...prevErrors, personImageUrl: "" }));
  };

  const handleMediaRemove = () => {
    setFormData((prevData) => ({
      ...prevData,
      imageFile: null,
      imagePreview: "",
      personImageUrl: "",
    }));
    setSelectedMedia(null);
    setErrors((prevErrors) => ({ ...prevErrors, personImageUrl: "" }));
  };

  const handleImageDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsImageDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    setIsImageUploading(true);
    try {
      const uploaded = await uploadDroppedMediaFile(file);
      if (uploaded) {
        handleMediaSelect(uploaded);
      }
    } finally {
      setIsImageUploading(false);
    }
  };

  const personImageLibrary = useMediaLibraryIntegration({
    media: selectedMedia,
    imageUrl: formData.personImageUrl,
    defaultKeyword: formData.personName,
    onSelect: handleMediaSelect,
    onRemove: handleMediaRemove,
  });

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { valid, errors: validationErrors } =
        validateTestimonialMaster(formData);
      setErrors(validationErrors);

      if (!valid) return;

      try {
        setIsButtonDisabled(true);
        const testimonialId = editMode
          ? (initialData?.id ?? initialData?.Id)
          : undefined;
        const payload = buildTestimonialFormData(
          formData,
          editMode,
          testimonialId
        );
        const action = editMode ? updateTestimonial : addTestimonial;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again."
          );
          return;
        }

        toast.success(
          `Testimonial ${editMode ? "updated" : "added"} successfully!`
        );
        resetForm();
        onSuccess?.();
        setEditMode?.(false);
        setSelectedTestimonial?.(null);
        onClose?.();
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [
      formData,
      editMode,
      initialData,
      onSuccess,
      setEditMode,
      setSelectedTestimonial,
      onClose,
      resetForm,
    ]
  );

  return (
    <FormSideDrawer
      isOpen={isOpen}
      onClose={closeDrawer}
      title={editMode ? "Update Testimonial" : "Add Testimonial"}
      subtitle={
        editMode
          ? "Edit testimonial details and save changes"
          : "Fill in the details to create a new testimonial"
      }
      icon={editMode ? "ri-pencil-line" : "ri-chat-quote-line"}
      width="md"
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={closeDrawer}
            disabled={isButtonDisabled}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="testimonial-side-form"
            className="btn btn-secondary"
            disabled={isButtonDisabled}
          >
            {isButtonDisabled
              ? editMode
                ? "Updating..."
                : "Saving..."
              : editMode
                ? "Update"
                : "Save"}
          </button>
        </>
      }
    >
      <form id="testimonial-side-form" onSubmit={handleSubmit} method="POST">
        <div className="row g-3">
          <div className="col-12">
            <label htmlFor="personName" className="form-label">
              Person Name <span className="required-field">*</span>
            </label>
            <input
              type="text"
              name="personName"
              id="personName"
              placeholder="Enter person name"
              value={formData.personName}
              onChange={handleInputChange}
              className={`form-control ${
                errors.personName ? "is-invalid" : ""
              }`}
            />
            {errors.personName && (
              <div className="invalid-feedback">{errors.personName}</div>
            )}
          </div>

          <div className="col-12">
            <label htmlFor="personDesignation" className="form-label">
              Designation
            </label>
            <input
              type="text"
              name="personDesignation"
              id="personDesignation"
              placeholder="e.g. CEO, Marketing Director"
              value={formData.personDesignation}
              onChange={handleInputChange}
              className={`form-control ${
                errors.personDesignation ? "is-invalid" : ""
              }`}
            />
            {errors.personDesignation && (
              <div className="invalid-feedback">
                {errors.personDesignation}
              </div>
            )}
          </div>

          <div className="col-12">
            <label htmlFor="displayOrder" className="form-label">
              Display Order <span className="required-field">*</span>
            </label>
            <input
              type="number"
              name="displayOrder"
              id="displayOrder"
              min="1"
              step="1"
              placeholder="e.g. 1"
              value={formData.displayOrder}
              onChange={handleDisplayOrderChange}
              onKeyDown={handleDisplayOrderKeyDown}
              className={`form-control ${
                errors.displayOrder ? "is-invalid" : ""
              }`}
            />
            {errors.displayOrder && (
              <div className="invalid-feedback">{errors.displayOrder}</div>
            )}
          </div>

          <div className="col-12">
            <label htmlFor="testimonialText" className="form-label">
              Testimonial Text <span className="required-field">*</span>
            </label>
            <textarea
              name="testimonialText"
              id="testimonialText"
              rows="5"
              placeholder="Enter the testimonial quote or feedback"
              value={formData.testimonialText}
              onChange={handleInputChange}
              className={`form-control ${
                errors.testimonialText ? "is-invalid" : ""
              }`}
            />
            {errors.testimonialText && (
              <div className="invalid-feedback">{errors.testimonialText}</div>
            )}
          </div>

          <div className="col-12">
            <label className="form-label mb-2 d-block">
              Person Image
              {editMode && (
                <span className="text-muted small fw-normal ms-1">
                  (optional — leave unchanged to keep current)
                </span>
              )}
            </label>

            {formData.imagePreview ? (
              <div className="ts-avatar-upload--filled">
                <div className="ts-avatar-upload__preview">
                  <span className="ts-avatar-upload__badge ts-avatar-upload__badge--ml">
                    <i
                      className="ri-folder-image-line"
                      aria-hidden="true"
                    />
                    Media Library
                  </span>
                  <img
                    src={formData.imagePreview}
                    alt="Person preview"
                    className="ts-avatar-upload__img"
                  />
                </div>
                <div className="ts-avatar-upload__info">
                  <p className="ts-avatar-upload__current">
                    {editMode ? "Current Image" : "Selected Image"}
                  </p>
                  <div className="ts-avatar-upload__actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm mb-0"
                      onClick={personImageLibrary.openLibrary}
                    >
                      <i className="ri-image-edit-line" />
                      Replace
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={handleMediaRemove}
                    >
                      <i className="ri-delete-bin-6-line" />
                      Remove Image
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div
                role="button"
                tabIndex={0}
                className={`ts-avatar-upload__dropzone ${
                  isImageDragging ? "is-dragging" : ""
                } ${isImageUploading ? "is-uploading" : ""} ${
                  errors.personImageUrl ? "is-error" : ""
                }`}
                onClick={
                  isImageUploading
                    ? undefined
                    : personImageLibrary.openLibrary
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    personImageLibrary.openLibrary();
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsImageDragging(true);
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsImageDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsImageDragging(false);
                }}
                onDrop={handleImageDrop}
              >
                {isImageUploading ? (
                  <>
                    <span className="ts-avatar-upload__icon">
                      <span
                        className="ts-avatar-upload__spinner"
                        aria-hidden="true"
                      />
                    </span>
                    <span className="ts-avatar-upload__title">
                      Uploading image...
                    </span>
                  </>
                ) : (
                  <>
                    <span className="ts-avatar-upload__icon">
                      <i className="ri-image-add-line" />
                    </span>
                    <span className="ts-avatar-upload__title">
                      Drag &amp; Drop Image Here
                    </span>
                    <span className="ts-avatar-upload__subtitle">
                      or Browse Files
                    </span>
                    <span className="ts-avatar-upload__formats">
                      JPG • JPEG • PNG • WEBP
                    </span>
                    <span className="ts-avatar-upload__rec">
                      Recommended: Max 2MB
                    </span>
                  </>
                )}
              </div>
            )}

            <p className="ts-avatar-upload__hint">
              JPG • JPEG • PNG • WEBP • Recommended: Max 2MB
            </p>
            {errors.personImageUrl && (
              <p className="ts-avatar-upload__error">
                {errors.personImageUrl}
              </p>
            )}
            {personImageLibrary.modalSlot}
          </div>
        </div>
      </form>
    </FormSideDrawer>
  );
};
